import {ApplicationRef, Component, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import {Location, ShiftWithDriverAndVehicleModel} from '../../../shared/models/shift.model';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {ShiftsService} from '../../../data/shifts/shifts.service';
import {ObservationsService} from '../../../data/observations/observations.service';
import {ConfigurationModel, FeatureFlagEnum} from '../../../shared/models/configuration.model';
import {ConfigurationService} from '../../../configuration/configuration.service';
import {ToastService} from '../../../shared/services/toast.service';
import {DrawerContent} from '../../../layouts/right-drawer/right-drawer.component';
import {ShiftMapDataService} from '../services/shift-map-data.service';
import {ObservationManagementService} from '../../../data/observations/observation-management.service';
import {ImagesManagerService} from '../../../data/images/images-manager.service';
import {ShiftMapContent} from '../../../shared/components/map-preview/model/map-content/ShiftMapContent';
import {Feature, FeatureCollection} from 'geojson';
import {SettingsService} from '../../../configuration/settings.service';
import {ObservationTypeGroup} from '../../../shared/models/observation-group';
import {Observation, ObservationType} from '../../../shared/models/observation';
import {JsonApiResponse} from '../../../shared/models/JsonApiResponse';
import {ImageModel} from '../../../shared/models/image';
import {ImageService} from '../../../data/images/image.service';
import {LocationApiService} from '../../../data/location-api/location-api.service';
import { filter, startWith } from 'rxjs/operators';
import { RouteAssignment } from '../../../shared/models/route-assignment';
import { RouteAssignmentService } from '../../../data/routes/route-assignment.service';
import { RouteSourceService } from '../../../shared/components/map-viewer/services/route-source.service';
import { RoutesManagerService } from '../../../data/routes/routes-manager.service';
import { RouteConfigurationWithSchema, RouteHierarchyItemWithPath } from '../../../shared/models/route';
import { InsightsRoute } from '../../insights/insights-routing.module';
import { ShiftLookupCategory } from '../../insights/components/shift-lookup/shift-lookup.component';
import { MainRoute, RootRoute } from '../../../shared/models/angular-routing';
import { PointFeature } from '../../../shared/models/GeoJson';
import { HardwareConfiguration, SensorType } from '../../../shared/models/vehicle.model';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { ShiftMapPanelComponent } from './shift-map-panel/shift-map-panel.component';
import { AssignmentMapPanelComponent } from './assignment-map-panel/assignment-map-panel.component';
import { BreadcrumbInfoComponent } from './breadcrumb-info/breadcrumb-info.component';
import { TimeLineControlComponent } from '../../../shared/components/time-line-control/time-line-control.component';
import { MapPreviewComponent } from '../../../shared/components/map-preview/map-preview.component';
import { ImageViewerComponent } from '../../../shared/components/image-viewer/image-viewer.component';

@Component({
  selector: 'app-shift-map',
  templateUrl: './shift-map.component.html',
  styleUrls: ['./shift-map.component.scss'],
  imports: [
    MatIcon,
    MatButton,
    MatIconButton,
    MatTooltip,
    ShiftMapPanelComponent,
    AssignmentMapPanelComponent,
    BreadcrumbInfoComponent,
    TimeLineControlComponent,
    MapPreviewComponent,
    ImageViewerComponent,
  ],
})
export class ShiftMapComponent implements OnInit, OnDestroy {
  configuration: ConfigurationModel;
  activeDrawer: DrawerContent;

  shiftId: number;
  assignmentId: number;
  insightRoute: string;
  source: string;

  private filterFrom = 0;
  private filterTo = 100;
  private speedFactor = 10; // 1 = normal time, 10 = 10x faster
  shift: ShiftWithDriverAndVehicleModel;
  shiftImages: ImageModel[];
  images: ImageModel[];
  shiftObservations: Observation[];
  observations: Observation[];
  assignments: RouteAssignment[];
  assignment: RouteAssignment;
  routeGeometries: FeatureCollection;
  routeConfigurations: RouteConfigurationWithSchema[];
  routes: RouteHierarchyItemWithPath[];

  private readonly observationTypeGroupsMap = new Map<number, ObservationTypeGroup>();
  private readonly observationTypesMap = new Map<number, ObservationType>();

  shiftMapContent: ShiftMapContent;
  mapContent: ShiftMapContent;

  vehicleMarkerFeature: PointFeature;
  vehicleMarkerUnixTime: number;
  shiftTrack: FeatureCollection;
  filteredTrack: FeatureCollection;

  isLoading = true;

  DrawerContent = DrawerContent;
  FeatureFlagEnum = FeatureFlagEnum;
  InsightsRoute = InsightsRoute;
  HardwareConfiguration = HardwareConfiguration;
  SensorType = SensorType;

  private readonly openSubscriptions = new Array<Subscription>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private configurationService: ConfigurationService,
    private shiftService: ShiftsService,
    private shiftMapDataService: ShiftMapDataService,
    private observationService: ObservationsService,
    private observationManagementService: ObservationManagementService,
    private imagesService: ImageService,
    private imageManager: ImagesManagerService,
    private routeAssignmentService: RouteAssignmentService,
    private routeSourceService: RouteSourceService,
    private routesManager: RoutesManagerService,
    private locationApi: LocationApiService,
    private toast: ToastService,
    private settingsService: SettingsService,
    private viewContainerRef: ViewContainerRef,
    private appRef: ApplicationRef,
  ) {}

  ngOnInit(): void {
    const configuration$ = this.configurationService.sharedConfigurationModel.pipe(filter(model => !!model));
    const navigation$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null)
    );

    const mainSubscription = combineLatest([configuration$, navigation$]).subscribe(([model]) => {
      this.configuration = model;
      this.insightRoute = this.router.url.split(/[\/?]+/)[3]?.toLowerCase();

      const newShiftId = +this.activatedRoute.snapshot.paramMap.get('shiftId');
      const newAssignmentId = +this.activatedRoute.firstChild?.snapshot?.paramMap?.get('assignmentId') || 0;

      if (!newShiftId) {
        return;
      }

      const shiftChanged = newShiftId !== this.shiftId;
      this.shiftId = newShiftId;
      this.assignmentId = newAssignmentId;

      if (shiftChanged || !this.assignments) {
        this.isLoading = true;
        Promise.all([
          this.shiftService.getShiftInfo(this.shiftId),
          this.observationService.getObservationTypes(),
          this.observationManagementService.getObservationTypeGroups(),
          // keep same value as in LiveMapComponent as it keeps the main list loaded
          // mainly the includedNNN should be same
          this.routeAssignmentService.getRouteAssignments(
            null,
            null,
            this.shiftId,
            null,
            null,
            true,
            false,
            false
          ),
        ]).then(responses => {
          const [shiftInfoResponse, observationTypes, observationTypeGroups, assignments] = responses;

          this.shift = shiftInfoResponse.data;
          this.shiftMapDataService.sendShift(this.shift);

          // load observation type groups
          for (const observationTypeGroup of observationTypeGroups) {
            this.observationTypeGroupsMap.set(observationTypeGroup.id, observationTypeGroup);
          }

          // load observation types
          for (const observationType of observationTypes) {
            this.observationTypesMap.set(
              observationType.id,
              new ObservationType(
                observationType.id,
                observationType.title,
                !!observationType.abbreviation ? observationType.abbreviation : observationType.title,
                observationType.observationTypeGroupId
              )
            );
          }
          // add deleted observation type
          this.observationTypesMap.set(-1, new ObservationType(-1, 'Deleted Observation Type', 'DELETED TYPE', -1));

          // assignments
          this.assignments = assignments.data;

          // get route geometries from "Live Map" service
          this.routeGeometries = this.routeSourceService.getFeatureCollection();

          // get route configurations from "Live Map" manager service
          this.routeConfigurations = this.routesManager.getRouteConfigurations();

          // get routes from "Live Map" manager service
          this.routes = this.routesManager.getRouteHierarchyLeaves();

          this.initializeMapContent();
          this.isLoading = false;
        })
          .catch(message => {
            console.error(message);
            this.isLoading = false;
          });
      } else {
        // Only assignment changed or configuration refreshed without shift change
        if (this.assignmentId > 0) {
          this.loadAssignmentMapContent();
        } else {
          this.unloadAssignmentMapContent();
        }
      }
    });
    this.openSubscriptions.push(mainSubscription);

    this.activatedRoute.queryParams.subscribe(params => {
      this.activeDrawer = params.drawer;
      this.source = params.source;
    });

    const that = this;
    const settingsSubscription = this.settingsService.settingsChangedObservable.subscribe({
      next(newSettings) {
        if (newSettings.key === SettingsService.SHIFT_TRACK_TIME_FILTER) {
          const valueFromSettings = newSettings.value;
          if (valueFromSettings) {
            that.filterFrom = +valueFromSettings.substring(0, valueFromSettings.indexOf('_'));
            that.filterTo = +valueFromSettings.substring(valueFromSettings.indexOf('_') + 1);

            if (that.mapContent instanceof ShiftMapContent) {
              that.mapContent.filterByTime(that.filterFrom, that.filterTo);
            } else {
              console.warn('empty map content while setting filtering by time');
            }
          }
        }
        if (newSettings.key === SettingsService.SHIFT_TRACK_ANIM_SPEED) {
          const valueFromSettings = newSettings.value;
          if (valueFromSettings) {
            that.speedFactor = +valueFromSettings;

            if (that.mapContent instanceof ShiftMapContent) {
              that.mapContent.changeAnimationSpeed(that.speedFactor);
            } else {
              console.warn('empty map content while setting speed factor');
            }
          }
        }
      },
    });
    this.openSubscriptions.push(settingsSubscription);
  }

  ngOnDestroy() {
    this.imageManager.release();
    this.openSubscriptions.forEach(subscription => subscription?.unsubscribe());
    this.shiftMapDataService.sendShift(null);
  }

  private initializeMapContent() {
    // first initialize ShiftMapContent
    Promise.all([
      this.locationApi.getTrackGeometries(this.shift.id, this.shift.vehicleId),
      this.locationApi.getShiftStatistics(this.shift.id),
      this.observationService.getObservationsByShiftId(this.shift.id),
      !!this.shift.vehicle.cameraConfiguration
        ? this.imagesService.getCadenceImagesPerShift(this.shift.id)
        : new Promise<JsonApiResponse<ImageModel[]>>(resolve => resolve({ data: [] } as JsonApiResponse<ImageModel[]>)),
    ])
      .then(responses => {
        const [trackResponse, statsResponse, observationsResponse, imagesResponse] = responses;
        this.shiftTrack = trackResponse;
        const markerFeatureCollection = {
          type: 'FeatureCollection',
          features: [],
        } as FeatureCollection;
        if (!!statsResponse.data && statsResponse.data.length > 0) {
          const shiftStats = statsResponse.data[0];
          if (!!shiftStats?.start?.coordinates?.longitude && !!shiftStats?.start?.coordinates?.latitude) {
            markerFeatureCollection.features.push(this.locationToMarkerFeature(shiftStats.start, { vehicle: false, start: true }));
            const vehiclePointFeature = this.locationToMarkerFeature(shiftStats.start, {
              vehicle: true,
              start: false,
              heading: this.getHeadingFromTrackStart(trackResponse),
            });
            markerFeatureCollection.features.push(vehiclePointFeature);
            if (!this.vehicleMarkerFeature && !this.assignmentId) {
              this.vehicleMarkerFeature = vehiclePointFeature as PointFeature;
              this.vehicleMarkerFeature.properties.speed = 0;
              this.vehicleMarkerFeature.properties.unixtime = shiftStats.start.unixtime;
            }
          }
          if (!!shiftStats?.end?.coordinates?.longitude && !!shiftStats?.end?.coordinates?.latitude) {
            markerFeatureCollection.features.push(this.locationToMarkerFeature(shiftStats.end, { vehicle: false, start: false }));
          }
        }
        this.shiftObservations = observationsResponse.data.map(observationModel => {
          const type = this.observationTypesMap.get(observationModel.typeId) ?? this.observationTypesMap.get(-1);
          type.group = this.observationTypeGroupsMap.get(type.observationTypeGroupId);
          return Observation.fromModel(observationModel, type, this.shift.vehicle, this.shift.driver);
        });
        const observationFeatureCollection = {
          type: 'FeatureCollection',
          features: this.shiftObservations.map(o => this.observationToObservationFeature(o)),
        } as FeatureCollection;

        this.shiftImages = imagesResponse.data;
        const imageFeatureCollection = {
          type: 'FeatureCollection',
          features: this.shiftImages.map(i => this.imageToImageFeature(i)),
        } as FeatureCollection;

        this.shiftMapContent = new ShiftMapContent(
          trackResponse,
          markerFeatureCollection,
          observationFeatureCollection,
          imageFeatureCollection,
          this.routeGeometries,
          this.configuration,
          this.configurationService.trackStyles,
          this.routeConfigurations,
          this.routes,
          this.shiftObservations,
          this.shift,
          null,
          this.locationApi,
          this.toast,
          this.viewContainerRef,
          this.appRef,
          this.shiftMapDataService,
          (feature, unixTimeMs) => {
            this.vehicleMarkerClicked(feature, unixTimeMs);
          }
        );
        if (this.assignmentId > 0) {
          this.loadAssignmentMapContent();
        } else {
          this.loadShiftMapContent();
        }
      })
      .catch(error => {
        const msg = 'Error while loading shift geometries.';
        this.toast.long(msg);
        console.error(`${msg} :: ${error}`);
      });
  }

  private loadAssignmentMapContent() {
    if (!this.assignments || !this.assignmentId) {
      return;
    }
    this.assignment = this.assignments.find(a => a.id === this.assignmentId);
    Promise.all([
      this.locationApi.getTrackGeometries(this.shift.id, this.shift.vehicleId, this.assignment.onAssignmentFrom, this.assignment.completed),
      this.locationApi.getShiftStatistics(this.shift.id, this.assignment.onAssignmentFrom, this.assignment.completed),
    ]).then(responses => {
      const [trackResponse, statsResponse] = responses;
      const markerFeatureCollection = {
        type: 'FeatureCollection',
        features: [],
      } as FeatureCollection;
      if (!!statsResponse.data && statsResponse.data.length > 0) {
        const shiftStats = statsResponse.data[0];
        if (!!shiftStats?.start?.coordinates?.longitude && !!shiftStats?.start?.coordinates?.latitude) {
          markerFeatureCollection.features.push(this.locationToMarkerFeature(shiftStats.start, { vehicle: false, start: true }));
          const vehiclePointFeature = this.locationToMarkerFeature(shiftStats.start, {
            vehicle: true,
            start: false,
            heading: this.getHeadingFromTrackStart(trackResponse),
          });
          markerFeatureCollection.features.push(vehiclePointFeature);
          if (!this.vehicleMarkerFeature && this.assignmentId > 0) {
            this.vehicleMarkerFeature = vehiclePointFeature as PointFeature;
            this.vehicleMarkerFeature.properties.speed = 0;
            this.vehicleMarkerFeature.properties.unixtime = shiftStats.start.unixtime;
          }
        }
        if (!!shiftStats?.end?.coordinates?.longitude && !!shiftStats?.end?.coordinates?.latitude) {
          markerFeatureCollection.features.push(this.locationToMarkerFeature(shiftStats.end, { vehicle: false, start: false }));
        }
      }

      // get assignment observations
      this.observations = this.shiftObservations.filter(o => {
        return (
          new Date(this.assignment.onAssignmentFrom) <= o.created &&
          (!this.assignment.completed || new Date(this.assignment.completed) >= o.created)
        );
      });
      const observationFeatureCollection = {
        type: 'FeatureCollection',
        features: this.observations.map(o => this.observationToObservationFeature(o)),
      } as FeatureCollection;

      // get assignment images
      this.images = this.shiftImages.filter(i => {
        return this.assignment.onAssignmentFrom <= i.time && this.assignment.completed >= i.time;
      });
      const imageFeatureCollection = {
        type: 'FeatureCollection',
        features: this.images.map(i => this.imageToImageFeature(i)),
      } as FeatureCollection;

      this.mapContent = new ShiftMapContent(
        trackResponse,
        markerFeatureCollection,
        observationFeatureCollection,
        imageFeatureCollection,
        this.routeGeometries,
        this.configuration,
        this.configurationService.trackStyles,
        this.routeConfigurations,
        this.routes,
        this.observations,
        this.shift,
        this.assignment,
        this.locationApi,
        this.toast,
        this.viewContainerRef,
        this.appRef,
        this.shiftMapDataService,
        (feature, unixTimeMs) => {
          this.vehicleMarkerClicked(feature, unixTimeMs);
        }
      );
      this.filteredTrack = trackResponse;
    });
  }

  private unloadAssignmentMapContent() {
    this.assignment = null;
    this.loadShiftMapContent();
  }

  private loadShiftMapContent() {
    this.mapContent = this.shiftMapContent;
    this.images = this.shiftImages;
    this.observations = this.shiftObservations;
    this.filteredTrack = this.shiftTrack;
  }

  private getHeadingFromTrackStart(featureCollection: FeatureCollection): number {
    if (featureCollection.features.length > 0) {
      const firstFeature = featureCollection.features[0];
      if (firstFeature.geometry.type === 'LineString') {
        const lineStringCoords = firstFeature.geometry.coordinates;
        if (lineStringCoords instanceof Array && lineStringCoords.length > 1) {
          return this.calculateHeading(lineStringCoords[0][0], lineStringCoords[0][1], lineStringCoords[1][0], lineStringCoords[1][1]);
        }
      } else if (firstFeature.geometry.type === 'MultiLineString') {
        const multiLineStringCoords = firstFeature.geometry.coordinates;
        if (
          multiLineStringCoords instanceof Array &&
          multiLineStringCoords.length > 0 &&
          multiLineStringCoords[0] instanceof Array &&
          multiLineStringCoords[0].length > 1
        ) {
          return this.calculateHeading(
            multiLineStringCoords[0][0][0],
            multiLineStringCoords[0][0][1],
            multiLineStringCoords[0][1][0],
            multiLineStringCoords[0][1][1]
          );
        }
      }
    }
    return undefined;
  }

  private calculateHeading(lon1: number, lat1: number, lon2: number, lat2: number): number {
    // Convert degrees to radians
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const lonDiff = ((lon2 - lon1) * Math.PI) / 180;

    // Calculate bearing/heading using the formula
    const y = Math.sin(lonDiff) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(lonDiff);

    const bearingRad = Math.atan2(y, x);

    // Convert radians to degrees and normalize to 0-360
    return ((bearingRad * 180) / Math.PI + 360) % 360;
  }

  private locationToMarkerFeature(location: Location, properties: { vehicle: boolean; start: boolean; heading?: number }): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [location.coordinates.longitude, location.coordinates.latitude],
      },
      properties,
    };
  }

  private observationToObservationFeature(observation: Observation): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [observation.location.longitude, observation.location.latitude],
      },
      properties: {
        id: observation.id,
        title: observation.observationType.mapLabel,
        highlighted: false,
        color: !!observation.observationType.group?.mapColor ? observation.observationType.group.mapColor : undefined,
      },
    };
  }

  private imageToImageFeature(image: ImageModel): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [image.location.longitude, image.location.latitude],
      },
      properties: {
        id: image.id,
        time: new Date(image.time).valueOf() / 1000,
        highlighted: false,
      },
    };
  }

  goBack() {
    const queryParams = {};
    if (this.assignment) {
      if (this.insightRoute === InsightsRoute.SHIFT_DETAIL) {
        this.router.navigate(['/', RootRoute.MAIN]);
      } else {
        this.router.navigate(['../..'], { relativeTo: this.activatedRoute?.firstChild });
      }
      return;
    } else if (this.insightRoute === InsightsRoute.SHIFT_LOOKUP && !!this.source) {
      queryParams['category'] = this.source;
      queryParams['id'] = this.source === ShiftLookupCategory.DRIVERS ? this.shift.driverId : this.shift.vehicleId;
    } else if (this.insightRoute === InsightsRoute.STORM_DETAIL) {
      const stormId = this.activatedRoute.parent?.snapshot?.params['stormId'];
      this.router.navigate(
        [
          `/${RootRoute.MAIN}`,
          MainRoute.INSIGHTS,
          InsightsRoute.STORM_DETAIL,
          stormId,
          { outlets: { shift: null } },
        ]
      );
      return;
    }
    this.router.navigate(['../..'], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
  }

  closeInsight() {
    this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.INSIGHTS], {
      queryParamsHandling: 'merge',
      queryParams: { search: undefined, address: undefined, visit: undefined },
    });
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.configuration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  // shift track clicked and vehicle marker moved
  vehicleMarkerClicked(feature: PointFeature, unixTime: number) {
    this.vehicleMarkerFeature = feature;
    this.vehicleMarkerUnixTime = unixTime;
  }

  // time control marker moved
  onTimeUpdate(unixtime: number) {
    // this.vehicleMarkerFeature = null;
    this.vehicleMarkerUnixTime = unixtime;
    this.mapContent.changeVehiclePosition(unixtime);
  }

  onSpeedUpdate(speed: number) {
    this.speedFactor = speed;
    this.shiftMapContent.changeAnimationSpeed(this.speedFactor);
    this.mapContent.changeAnimationSpeed(this.speedFactor);
  }
}
